import * as $ from 'jquery';
import { IDisposable, Lazy, emptyDisposable, serialDisposable, disposableSubscription } from 'common/barrels/core';
import { BehaviorSubject } from 'common/barrels/rx';
import * as path from 'shared/path';
import * as moment from 'moment';

import { Control } from 'runtime/controls/control';
import { ControlArgs } from 'runtime/controls/controlargs';
import { ViewInfoRequested } from 'runtime/events/viewinforequested';
import { registerBinding } from 'runtime/bindingengine';
import { newGuid } from 'common/functions/guid';
import { FileFieldMetadata } from 'runtime/data/filefieldmetadata';
import { FileUploadComplete } from 'runtime/events/fileuploadcomplete';
import { FileUploadError } from '../events/fileuploaderror';
import { FileUploadVirusDetected } from 'runtime/events/fileuploadvirusdetected';
import { FileUploadStartRequested } from 'runtime/events/fileuploadstartrequested';
import { FileUploadCancelRequested } from 'runtime/events/fileuploadcancelrequested';
import { BindingInfo } from 'runtime/metadata/bindinginfo';
import * as rxui from 'common/rxui/core';

import 'runtime/styles/controls/filecontrol.less';

var lrmap = {
    drop: 'FileUploadDrop',
    empty: 'FileUploadStateEmpty',
    error: 'FileUploadStateError',
    virus: 'FileUploadStateVirusDetected',
    initializing: 'FileUploadStateInitializing',
    uploading: 'FileUploadStateUploadingNoProgress',
    bytes: 'UnitBytes',
    download: 'FileControlDownloadFile',
    remove: 'FileControlRemoveFile'
};

var sizeSuffixLrMap = [
    'UnitSymbolBytes',
    'UnitSymbolKilobytes',
    'UnitSymbolMegabytes',
    'UnitSymbolGigabytes',
    'UnitSymbolTerabytes',
    'UnitSymbolPetabytes',
    'UnitSymbolExabytes'
];

export class FileControl extends Control {
    private $inputLabel: JQuery;
    private $icon: JQuery;
    private $name: JQuery;
    private $size: JQuery;
    private $download: JQuery;

    private transferId: string;
    private fileContext: string;

    private fileFieldBindings = serialDisposable(null);
    private state: BehaviorSubject<string>;
    private uploadArtifacts: IDisposable;

    private metadata: FileFieldMetadata;
    private viewInfo: Lazy<ViewInfoRequested>;

    private enabledSubject = new BehaviorSubject(this.derivedEnabledState());

    private accepts: string;

    constructor(args: ControlArgs) {
        super(args);

        this.methods.register(this.config.elementName, this);

        this.$element.addClass('control-container filecontrol');

        var $borderContainer = $('<div />').addClass('filecontrol-border-container').appendTo(this.$element);

        var $tableContainer = $('<div />').addClass('filecontrol-table-container').appendTo($borderContainer);

        this.$inputLabel = $('<label />').appendTo($tableContainer).change(e => this.performUpload(e)).addClass('filecontrol-label');

        var $layout = $('<div />').addClass('filecontrol-layout-container').appendTo($tableContainer);
        var $iconContainer = $('<div />').addClass('filecontrol-icon').appendTo($layout);
        var $initializing = $('<span />').addClass('filecontrol-initializing').text(this.lr('initializing')).appendTo($layout);
        this.$icon = $('<img />').appendTo($iconContainer);
        this.$name = $('<span />').addClass('filecontrol-name').appendTo($layout);
        var $dropclick = $('<span />').addClass('filecontrol-dropclick').text(this.lr('drop')).appendTo($layout);
        var $noFile = $('<span />').addClass('filecontrol-nofile').text(this.lr('empty')).appendTo($layout);
        var $uploading = $('<span />').addClass('filecontrol-uploading').text(this.lr('uploading')).appendTo($layout);
        var $error = $('<span />').addClass('filecontrol-error').text(this.lr('error')).appendTo($layout);
        var $virus = $('<span />').addClass('filecontrol-virus').text(this.lr('virus')).appendTo($layout);
        this.$size = $('<span />').addClass('filecontrol-size').appendTo($layout);
        this.$download = $('<a />').append($('<i />').addClass('fa fa-arrow-circle-o-down')).prop('title', this.lr('download')).attr('tabindex', 0).appendTo($layout);
        var $delete = $('<i />').addClass('fa fa-remove').prop('title', this.lr('remove')).attr('tabindex', 0).appendTo($layout);

        this['content.Filter'](this.config['content.Filter']);

        this.prepareForInput();

        this.applyTooltip(this.$element);

        var stateMap = {
            'initializing': [[$initializing], [$initializing]],
            'empty': [[$dropclick, this.$inputLabel], [$noFile]],
            'uploaded': [[$iconContainer, this.$name, this.$size, this.$download, $delete], [$iconContainer, this.$name, this.$size, this.$download]],
            'uploading': [[$iconContainer, $uploading, $delete], [$iconContainer, $uploading]],
            'error': [[$error, $delete], [$error]],
            'virus': [[$virus, $delete], $virus]
        };

        this.uploadArtifacts = emptyDisposable;

        this.viewInfo = new Lazy(() => {
            var vir = new ViewInfoRequested();
            this.eventAggregator.publish(vir);
            return vir;
        });

        this.subscriptions = disposableSubscription(
            this.state = new BehaviorSubject('initializing'),
            this.state.combineLatest(this.enabledSubject, (x, y) => ({ state: x, enabled: y })).subscribe(x => {
                $layout.children().add(this.$inputLabel).hide();
                $.each(stateMap[x.state][x.enabled ? 0 : 1], (i, c) => c.show());
            }),
            this.state.pairwise().filter(x => x[0] === 'uploading' && x[1] === 'uploaded').subscribe(() => this.triggerEvent('FileControlFileUploadFinished')),
            this.state.filter(x => x === 'uploading').subscribe(() => this.triggerEvent('FileControlFileUploadStarted')),
            this.values.bindOneWay('FileFieldMetadata', args.fieldContext, this.fieldBindingSource('content.FileField'), x => {
                this.metadata = x;
                if (!this.accepts && this.metadata.allowedTypes)
                    this['content.Filter'](this.metadata.allowedTypes);
            }),
            rxui.click($delete).merge(rxui.enterPressed($delete)).merge(rxui.spacePressed($delete)).subscribe(() => this.clear()),
            rxui.enableDragAndDrop(this.$element).whereLatest(this.enabledSubject).subscribe(e => this.performUpload(e)),

            rxui.focusin(this.$element).subscribe(x => this.$element.addClass('filecontrol-focus')),
            rxui.focusout(this.$element).subscribe(x => this.$element.removeClass('filecontrol-focus')),

            // todo: fix this. Temporarily silenced because serverside issues with the file (such as it being damaged and served up) can lead to rapid/infinite callchains
            // rxui.error(this.$icon).subscribe(() => this.loadDefaultIcon()),
            this.eventAggregator.query(FileUploadComplete).filter(x => x.transferId === this.transferId).subscribe(x => this.uploadComplete(x.fileId, x.newName, x.newLength)),
            this.eventAggregator.query(FileUploadError).filter(x => x.transferId === this.transferId).subscribe(() => this.error()),
            this.eventAggregator.query(FileUploadVirusDetected).filter(x => x.transferId === this.transferId).subscribe(() => this.virusDetected()),
            this.processBindings(this.config),
            this.managePrimaryBinding('content.FileField')
        );

        this.initialize();
    }

    dispose() {
        super.dispose();
        this.fileFieldBindings.dispose();
    }

    private prepareForInput() {
        this.$inputLabel.empty().append($('<input type="file" name="file" id="file" />').attr('accept', this.accepts));
    }

    private performUpload(event) {
        var source = event.target instanceof HTMLInputElement ? event.target : event.dataTransfer;

        var fileName = source.files !== undefined ? source.files[0].name : path.fileName(source.value);
        var size = source.files !== undefined ? source.files[0].size : null;
            
        if (!this.validateFile(fileName, size))
            return;

        this.transferId = newGuid();

        this.$element.triggerHandler('fileName', fileName);
        this.$element.triggerHandler('fileSize', size);
        this.$element.triggerHandler('uploadUserId', this.viewInfo.value().currentUserId);
        this.$element.triggerHandler('uploadWorkItemId', this.viewInfo.value().workItemId);
        this.$element.triggerHandler('uploadDate', moment.utc().toISOString());

        this.state.next('uploading');
        this.eventAggregator.publish(new FileUploadStartRequested(this.transferId, source, null, null));
    }

    private uploadComplete(fileId, name, length) {
        this.$element.triggerHandler('fileId', fileId);
        this.$element.triggerHandler('fileName', name);
        this.$element.triggerHandler('fileSize', length);
    }

    private clear() {
        if (!!this.transferId)
            this.eventAggregator.publish(new FileUploadCancelRequested(this.transferId));

        this.transferId = null;

        this.$element.triggerHandler('fileName', [null]);
        this.$element.triggerHandler('fileSize', [null]);
        this.$element.triggerHandler('uploadUserId', [null]);
        this.$element.triggerHandler('uploadWorkItemId', [null]);
        this.$element.triggerHandler('uploadDate', [null]);
        this.$element.triggerHandler('fileId', [null]);

        this.prepareForInput();

        this.state.next('empty');
    }

    private updateIcon(fileName: string) {
        if (fileName === null || fileName === undefined)
            return;

        this.$icon.attr('src', path.icon(fileName));
    }

    private loadDefaultIcon() { this.$icon.attr('src', path.defaultIcon()); }

    private updateLink(fileId: string) {            
        var link = path.download(fileId, this.viewInfo.value().workItemId);
        this.$download.attr('href', link);
    }

    'content.Filter'(value: string) {
        this.accepts = this.getAllowedTypeSequence(value).map(x => '.' + x).join(',');
        this.prepareForInput();
    }

    'content.FileField'(value) {
        this.fileContext = value;

        this.fileFieldBindings.set(
            disposableSubscription(
                this.values.createBinding(new BindingInfo('Field', 'FileId', 'fileId', 'TwoWay', null), this, this.$element, this.fileContext),
                this.values.createBinding(new BindingInfo('Field', 'FileName', 'fileName', 'TwoWay', null), this, this.$element, this.fileContext),
                this.values.createBinding(new BindingInfo('Field', 'FileSize', 'fileSize', 'TwoWay', null), this, this.$element, this.fileContext),
                this.values.createBinding(new BindingInfo('Field', 'UploadUserId', 'uploadUserId', 'TwoWay', null), this, this.$element, this.fileContext),
                this.values.createBinding(new BindingInfo('Field', 'UploadWorkItemId', 'uploadWorkItemId', 'TwoWay', null), this, this.$element, this.fileContext),
                this.values.createBinding(new BindingInfo('Field', 'UploadDate', 'uploadDate', 'TwoWay', null), this, this.$element, this.fileContext)
            )
        );

        this.checkInitialValueReceived();
    }

    fileId(value) {
        this.updateLink(value);
        this.state.next(!!value ? 'uploaded' : 'empty');
    }

    fileName(value) {
        var text = (value === null || value === undefined) ? '' : value;
        this.$name.text(text).prop('title', text);
        this.updateIcon(value);
    }

    fileSize(value) {
        if (value === null || value === undefined) {
            this.$size.text('');
            return;
        }

        var e = value === 0 ? 0 : Math.floor(Math.log(value) / Math.log(1024));
        this.$size.text(+(value / Math.pow(1024, e)).toFixed(2) + ' ' + this.resources.getCoreString(sizeSuffixLrMap[e])).prop('title', value.toLocaleString() + ' ' + this.lr('bytes'));
    }

    validateFile(fileName: string, length: number) {
        // if we haven't managed to get access to the metadata at this point, we'll skip client-side validation; the server will still enforce all conditions
        if (!this.metadata)
            return true;

        var allowedTypes = this.metadata.allowedTypes;
        var allowedTypesSequence = allowedTypes != null ? this.getAllowedTypeSequence(allowedTypes) : null;

        const extension = path.getExtension(fileName).toLowerCase();

        if (allowedTypesSequence !== null && !allowedTypesSequence.some(x => x === extension)) {
            if (this.hasSubscriptions('FileControlFileTypeNotAllowed'))
                this.triggerEvent('FileControlFileTypeNotAllowed');
            else
                this.methods.invoke('Document', 'fileTypeNotAllowed', allowedTypesSequence.map(x => '.' + x), () => {});

            this.prepareForInput();

            return false;
        }

        if (!!length) {
            var maxSize = this.metadata.maxSize;
            if (maxSize !== null && length > maxSize) {
                if (this.hasSubscriptions('FileControlFileMaxSizeExceeded'))
                    this.triggerEvent('FileControlFileMaxSizeExceeded');
                else
                    this.methods.invoke('Document', 'maxFileSizeExceeded', null, () => { });

                this.prepareForInput();

                return false;
            }    
        }

        return true;
    }

    private getAllowedTypeSequence(source: string) {
        return (source || '').split(/;|,|\|/)
            .map(x => $.trim(x))
            .filter(x => x !== '')
            .map(x => x.indexOf('*') === 0 ? x.substring(1) : x)
            .map(x => x.indexOf('.') === 0 ? x.substring(1) : x)
            .map(x => x.toLowerCase())
            .sort();
    }

    private virusDetected() {
        this.state.next('virus');

        if (this.hasSubscriptions('FileControlVirusDetected'))
            this.triggerEvent('FileControlVirusDetected');
    }

    private error() {
        this.state.next('error');

        if (this.hasSubscriptions('FileControlFileUploadError'))
            this.triggerEvent('FileControlFileUploadError');
    }

    // these aren't actually used at all in rendering, and there's no use case for pushing them back into the value store
    uploadUserId(value) { }
    uploadDate(value) { }
    uploadWorkItemId(value) { }

    focus() {
        this.$inputLabel.find('input').focus();
    }

    applyEnabledState(value: boolean) {
        this.enabledSubject.next(value);
    }

    private lr(moniker: string) {
        return this.resources.getCoreString(lrmap[moniker]);
    }
}

registerBinding('filecontrol', FileControl);